
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import RuleTabs from '@components/settings/rule/RuleTabs.vue';
import { LoadState } from '@/utils/types';
import { RuleV2, RuleV2Mutation, TripLog } from '@/generated-clients/myTrips';
import deepCopy from '@/utils/copy';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'RuleEdit',
  components: {
    AppPage,
    AppContent,
    AppEditableHeader,
    SettingsTitleBar,
    SettingsFooter,
    SettingsDelete,
    InfoBoxWarning,
    RuleTabs
  }
})
export default class RuleEdit extends Vue {
  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Action('updateRuleV2')
  private updateRule;

  @SettingsModule.Action('deleteRule')
  private deleteRule;

  @SettingsModule.Getter('getRulesV2Status')
  private rulesStatus: LoadState;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus: LoadState;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @TriplogModule.Getter('getVehicles')
  private tripLogs: TripLog[];

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Getter('getPilots')
  private pilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Getter('getTags')
  private tags;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Getter('getZones')
  private zones;

  @SettingsModule.Getter('getTagsStatus')
  private tagsStatus: LoadState;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getZonesStatus')
  private zonesStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private rule: RuleV2 = null;
  private input: RuleV2Mutation = {
    name: '',
    active: true,
    channels: [],
    vehicleIds: [],
    triggers: [],
    zoneIds: [],
    pilotId: null,
    tagId: null,
    work: false,
    note: '',
    triggerTimeframe: null
  };

  private disabled = true;
  private requestErrorMessage = '';

  private deleteText = 'Sind Sie sicher, dass Sie die Regel löschen möchten?';

  async created() {
    this.fetchPilots();
    this.fetchTags();

    const [vehicles, rules, zones] = await Promise.all([this.fetchVehicles(), this.fetchRules(), this.fetchZones()]);
    this.rule = rules.find(p => p.id === this.id);
    this.input = deepCopy(this.rule);
    this.disabled = false;

    if (this.input.vehicleIds.length === vehicles.length) {
      this.input.vehicleIds = [];
    }
    if(this.input.zoneIds.length === zones.length && zones.length > 1) {
      this.input.zoneIds = [];
    }
  }

  get titleValue() {
    return this.isLoading ? '' : this.input.name;
  }

  get placeholder() {
    return this.isLoading ? 'Wird geladen...' : 'Name hier eingeben';
  }

  get backButton() {
    return { text: 'Zurück zu Regeln', location: this.backLocation };
  }

  get backLocation() {
    return { name: 'rulesOverview' };
  }

  get title() {
    return this.rule ? this.rule.name : '';
  }

  get id() {
    return this.$route.params.id;
  }

  get noActionSelectedError() {
    return !this.input.pilotId && !this.input.tagId && !this.input.note && (this.input.channels.length === 0 || !this.notificationsAvailable);
  }

  get nameError() {
    return this.input && this.input.name.length === 0;
  }

  get errorsExist() {
    return this.nameError || !!this.customErrorMessage;
  }

  get customErrorAndNoTitleErrorExists() {
    return !this.nameError && !!this.customErrorMessage;
  }

  get customErrorMessage() {
    if (this.input.triggers.length === 0) {
      return 'Es muss mindestens ein Auslöser ausgewählt sein (Abfahrt oder Ankunft).';
    } else if (this.noActionSelectedError) {
      return 'Es muss mindestens eine Aktion ausgewählt sein.';
    } else if (this.input.work && !this.input.note) {
      return 'Bei einer beruflichen Fahrt ist die Beschreibung erforderlich.';
    } else if (this.timeframeError) {
      return 'Die ausgewählte Zeitspanne ist nicht gültig.';
    }

    return undefined;
  }

  get timeframeError() {
    return this.input.triggerTimeframe && (Boolean(this.input.triggerTimeframe.from) !== Boolean(this.input.triggerTimeframe.until) ||
      this.input.triggerTimeframe.from === this.input.triggerTimeframe.until);
  }

  private async submit() {
    if (!this.errorsExist) {
      this.disabled = true;
      this.requestErrorMessage = '';

      const zones = this.input.zoneIds.length > 0 ? this.input.zoneIds : this.zones.map(zone => zone.id)
      const rule = { ...this.input, vehicleIds: this.selectedVehicles, zoneIds: zones };

      if (!this.notificationsAvailable) {
        rule.channels = [];
      }

      try {
        await this.updateRule({ id: this.rule.id, rule });
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        if (error.response.status === 409) {
          this.requestErrorMessage = 'Diese Regel kann nicht gespeichert werden, da sie Im Konflikt mit einer anderen Regel steht.';
        } else {
          this.requestErrorMessage = 'Fehler beim Speichern der Regel. Bitte versuchen sie es später erneut.';
        }
      } finally {
        this.disabled = false;
      }
    }
  }

  private deleteRuleButton() {
    if (window.confirm(this.deleteText)) {
      this.handleRuleDelete();
    }
  }

  private async handleRuleDelete() {
    this.disabled = true;
    try {
      await this.deleteRule({ id: this.rule.id });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } catch (error) {
      this.requestErrorMessage = 'Fehler beim Löschen der Regel. Bitte versuchen sie es später erneut.'
    } finally {
      this.disabled = false;
    }
  }

  get notificationsAvailable() {
    return this.tripLogs
        .filter(tripLog => tripLog.smartConnected)
        .some(smartConnectedTripLog => this.selectedVehicles.includes(smartConnectedTripLog.vehicle.id));
  }

  get selectedVehicles() {
    return this.input.vehicleIds.length > 0 ? this.input.vehicleIds : this.tripLogs.map(tl => tl.vehicle.id);
  }

  get loadingStatuses() {
    return [this.vehiclesStatus, this.tagsStatus, this.pilotsStatus, this.zonesStatus, this.rulesStatus];
  }

  get isLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Loading || status === LoadState.None);
  }

  get errorLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Error);
  }
}
